import { SKU } from '@talentinc/gatsby-source-talentinc-api/types'
import { graphql, useStaticQuery } from 'gatsby'
import isEqual from 'lodash/isEqual'
import useDiscountCode from './useDiscountCode'

export type Nullable<T> = { [K in keyof T]: Nullable<T[K]> | null }
export type NonNullish<T> = { [K in keyof T]: Exclude<T[K], null> }
export type SnakeCaseToCamelCase<T> = T extends `${infer U}_${infer V}`
  ? `${U}${Capitalize<SnakeCaseToCamelCase<V>>}`
  : T

export type AlternatePlan = Nullable<{
  amount: number
  interval_times: number
  plan_code: string
}>

export interface FormattedSKU extends SKU {
  originalAmount: number
  formattedOriginalAmount: string
  formattedAmount: string
  queryKeys: string[]
  discountedAmount?: number
  formattedDiscount?: string | null
  purchasePath?: string
  alternate_plan?: AlternatePlan
  currency_code: string
}

// @TODO: Could be converted to a separate hook
export const getOriginalSKU = (
  skus: FormattedSKU[],
  itemKeys: string[],
  planCode: string | null,
  requiredSort?: number | null
): FormattedSKU | undefined => {
  let skuUsingPlanCode = null

  if (planCode) {
    skuUsingPlanCode = skus.find((sku) => sku.plan_code === planCode)

    if (skuUsingPlanCode) {
      return skuUsingPlanCode
    }
  }

  let sortedItemKeys: string[] | null = null

  if (Array.isArray(itemKeys)) {
    sortedItemKeys = [...itemKeys].sort()
  }

  const sortSkus = skus.filter((skuToFilter) => skuToFilter.sort !== null)
  const getFallbackSku = () =>
    [...skus]
      // Return the highest priced sku
      .sort((a, b) => b.amount - a.amount)
      // whose item keys exactly match the query keys
      .find((skuToFind) => isEqual(sortedItemKeys, skuToFind.queryKeys))

  // TODO: fix itemKeys == sku.queryKeys for sku 'B0qd82z' without the hack

  if (sortSkus.length && requiredSort) {
    // requiredSort: The required Contentful sort
    // 1 - TR/TCV: Professional Growth/ZipJob: Launch
    // 2 - TR/TCV: Career Evolution/ZipJob: Fast Track
    // 3 - TR/TCV: Executive Priority/ZipJob: Premium
    return sortSkus.find((sortSku) => isEqual(sortSku.sort, requiredSort))
  }
  return getFallbackSku()
}

// @TODO Because this is all based around item keys and packages might not
// exist with a given set of keys, we need to be able to construct packages
// dynamically be merging SKUs together.
/**
 * useSKU is a hook that will provide the correct SKU for a given item type set.
 * This allows for site unaware linking to purchase pages in the correct
 * currency for a site from Contentful/Gatsby.
 *
 * Some example usages:
 *
 * const linkedInSKU = useSKU(['linkedin-document'])
 */
export function useSKU(
  itemKeys: string[],
  planCode: string | null,
  discountCode?: string | null,
  requiredSort?: number | null
): FormattedSKU | undefined {
  let sku: FormattedSKU | undefined
  const skus = useAllSKUs()

  const originalSKU = getOriginalSKU(skus, itemKeys, planCode, requiredSort)

  const {
    isLoading,
    isError,
    data: discount,
  } = useDiscountCode(originalSKU?.plan_code, discountCode)

  const currencyFormatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: originalSKU?.currency_code || 'USD',
    maximumFractionDigits: 0,
    currencyDisplay: 'narrowSymbol',
  })

  // We are going to mutate the SKU if we have one, so copy the object before
  // doing so.
  if (originalSKU) {
    sku = { ...originalSKU }
    sku.purchasePath = `/purchase/${sku.plan_code}`
    if (discountCode && !isLoading && !isError && discount) {
      sku.discountedAmount = discount.sticker_price - discount.discount_value
      sku.formattedDiscount = currencyFormatter.format(sku.discountedAmount)
      sku.amount = sku.discountedAmount
      sku.formattedAmount = sku.formattedDiscount
      sku.purchasePath += `?dt=${discountCode}`
    }
  }

  // Leave formatting to the client browser locale.
  // sku?.formattedAmount &&
  //   (sku.formattedAmount = sku.formattedAmount.replace('€', ' €'))
  // sku?.formattedOriginalAmount &&
  //   (sku.formattedOriginalAmount = sku.formattedOriginalAmount.replace('€', ' €'))
  // sku?.formattedDiscount &&
  //   (sku.formattedDiscount = sku.formattedDiscount.replace('€', ' €'))

  return sku
}

export function useAllSKUs(): FormattedSKU[] {
  const {
    allTalentIncSku: { skus },
  } = useStaticQuery<{ allTalentIncSku: { skus: SKU[] } }>(
    graphql`
      query UseAllSKUs {
        allTalentIncSku(limit: 999) {
          skus: nodes {
            sort
            plan_code
            amount
            currency_code
            item_keys
            contentful_item_keys
            plan_name
            plan_short_name
            alternate_plan {
              amount
              interval_times
              plan_code
            }
            short_pitch
            very_short_pitch
            item_details {
              id
              item_quantity
            }
          }
        }
      }
    `
  )

  return skus.map((sku) => {
    let queryKeys = [...sku.item_keys]

    const currencyFormatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: sku.currency_code,
      maximumFractionDigits: 0,
      currencyDisplay: 'narrowSymbol',
    })

    const rightAlignEuroCurrencySymbol = (amount: number) => {
      const parts = currencyFormatter.formatToParts(amount).map((p) => p.value)
      const formAmount = parts.join('')
      return formAmount
    }

    const getFormattedAmount = (sku: SKU) =>
      sku.currency_code === 'EUR'
        ? rightAlignEuroCurrencySymbol(sku.amount)
        : currencyFormatter.format(sku.amount)

    // In general, we can map items by item_keys, but there are specialized
    // versions of items, like federal resumes or customized thank you letters,
    // that we will need to target. We accomplish this by having specialized
    // contentful_item_keys (which look like resume:federal and
    // thank-you-letter:customized) and subbing them into a field called
    // queryKeys, replacing their original versions (resume and
    // thank-you-letter, in the above examples).
    sku.contentful_item_keys.forEach((contentfulItemKey) => {
      const itemKeySplit = contentfulItemKey.split(':')
      if (itemKeySplit.length > 1) {
        const baseItemIndex = queryKeys.indexOf(itemKeySplit[0])
        if (baseItemIndex > -1) {
          queryKeys[baseItemIndex] = contentfulItemKey
        }
      }
    })

    if (sku.plan_code === 'B0qd82z') {
      // queryKeys override hack for Execute Resume Service Zipjob plan_code 'B0qd82z'
      // queryKeys does not match itemKeys in the useSKU function, which is causing an empty price for 'B0qd82z'
      queryKeys = [
        'cover-letter',
        'guarantee:60-days',
        'linkedin-document',
        'phone-call',
        'resume',
        'thank-you-letter:customized',
      ]
    }

    // Sort them alphabetically so that isEqual can compare them.
    queryKeys.sort()

    return {
      ...sku,
      originalAmount: sku.amount,
      formattedOriginalAmount: getFormattedAmount(sku),
      formattedAmount: getFormattedAmount(sku),
      queryKeys,
    }
  })
}
